import React, { JSXElementConstructor, ReactElement, useState } from "react";
import classNames from "classnames";
import { Controller, FieldValues, Path, Control } from "react-hook-form";
import { Button, Tooltip } from "Components/ReactstrapSlim";
import { useUniqueId } from "Hooks";
import { SpaceType } from "Services/API/API";
import * as SpaceTypeHelper from "Models/SpaceType";

interface SpaceTypeTogglesProps<TFieldValues extends FieldValues = Record<string, any>, TName = string>{
   control: Control<TFieldValues>,
   name: TName,
   spaceType?: SpaceType
}

const SpaceTypeToggles: React.FC<SpaceTypeTogglesProps> = ({ control, name, spaceType }) => {

   const deskSpaceTypesId = useUniqueId("space_type_");
   const officeSpaceTypesId = useUniqueId("space_type_");
   const meetingSpaceTypesId = useUniqueId("space_type_");

   const [isDeskSpaceTypeTooltipOpen, setDeskSpaceTypeTooltipOpen] = useState(false);
   const [isOfficeSpaceTypeTooltipOpen, setOfficeSpaceTypeTooltipOpen] = useState(false);
   const [isMeetingSpaceTypeTooltipOpen, setMeetingSpaceTypeTooltipOpen] = useState(false);

   const isDeskDisabled = spaceType != undefined && !SpaceTypeHelper.contains(spaceType, SpaceType.Desk);
   const isOfficeDisabled = spaceType != undefined && !SpaceTypeHelper.contains(spaceType, SpaceType.Office);
   const isMeetingDisabled = spaceType != undefined && !SpaceTypeHelper.isMeetingAggregated(spaceType);

   return <>
      <Controller
         control={control}
         name={name}
         render={({ field }) => <>
            <div className="space-types">
               <div id={deskSpaceTypesId} onMouseEnter={() => setDeskSpaceTypeTooltipOpen(true)} onMouseLeave={() => setDeskSpaceTypeTooltipOpen(false)}>
                  <Button className="space-type-toggle" size="sm" disabled={isDeskDisabled} 
                     onClick={event => { event.preventDefault(); field.onChange(SpaceTypeHelper.toggle(field.value, SpaceType.Desk)) }} color={SpaceTypeHelper.contains(field.value, SpaceType.Desk) ? "white" : "transparent"}>Desk</Button>
               </div>
               <div className={classNames("space-types-separator", { "space-types-separator--transparent": SpaceTypeHelper.contains(field.value, SpaceType.Desk) || SpaceTypeHelper.contains(field.value, SpaceType.Office) })} />
               <div id={officeSpaceTypesId} onMouseEnter={() => setOfficeSpaceTypeTooltipOpen(true)} onMouseLeave={() => setOfficeSpaceTypeTooltipOpen(false)}>
                  <Button className="space-type-toggle" size="sm" disabled={isOfficeDisabled} 
                     onClick={event => { event.preventDefault(); field.onChange(SpaceTypeHelper.toggle(field.value, SpaceType.Office)) }} color={SpaceTypeHelper.contains(field.value, SpaceType.Office) ? "white" : "transparent"}>Office</Button>
               </div>
               <div className={classNames("space-types-separator", { "space-types-separator--transparent": SpaceTypeHelper.contains(field.value, SpaceType.Office) || SpaceTypeHelper.isMeetingAggregated(field.value) })} />
               <div id={meetingSpaceTypesId} onMouseEnter={() => setMeetingSpaceTypeTooltipOpen(true)} onMouseLeave={() => setMeetingSpaceTypeTooltipOpen(false)}>
                  <Button className="space-type-toggle" size="sm" disabled={isMeetingDisabled} 
                     onClick={event => { event.preventDefault(); field.onChange(SpaceTypeHelper.toggle(field.value, ...SpaceTypeHelper.MeetingAggregatedSpaceType)) }} color={SpaceTypeHelper.isMeetingAggregated(field.value) ? "white" : "transparent"}>Meet</Button>
               </div>
            </div>
         </>
         }
      />

      <Tooltip target={deskSpaceTypesId} isOpen={isDeskDisabled && isDeskSpaceTypeTooltipOpen}>
         Desk space type is not currently supported by your company.
      </Tooltip>
      <Tooltip target={officeSpaceTypesId} isOpen={isOfficeDisabled && isOfficeSpaceTypeTooltipOpen}>
         Office space type is not currently supported by your company.
      </Tooltip>
      <Tooltip target={meetingSpaceTypesId} isOpen={isMeetingDisabled && isMeetingSpaceTypeTooltipOpen}>
         Meet space type is not currently supported by your company.
      </Tooltip>
   </>
}

export default SpaceTypeToggles as <TFieldValues extends FieldValues = FieldValues, TName extends Path<TFieldValues> = Path<TFieldValues>>(props: SpaceTypeTogglesProps<TFieldValues, TName>) => ReactElement<any, string | JSXElementConstructor<any>>;
